import { ensureTimestamp } from "@/lib/utils";
import { Job } from "@/schemas/firestore/jobs.schema";

const GENERATION_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export function hasActivePendingJobs(jobs: Job[]): boolean {
  // Defensive check: if jobs array is empty or undefined, return false
  if (!jobs || jobs.length === 0) {
    return false;
  }

  const activePendingJobs = jobs.filter((job) => {
    if (job.status !== "pending") return false;

    // Check if generation is stale (older than timeout)
    if (job.created_at && isJobGenerationStale(job.created_at)) {
      return false;
    }

    return true;
  });

  return activePendingJobs.length > 0;
}

export function isJobGenerationStale(createdAt: unknown): boolean {
  // Handle null, undefined, or invalid timestamps
  if (!createdAt) {
    return false; // Don't consider it stale if we don't have a valid timestamp
  }

  const timestamp = ensureTimestamp(createdAt);
  if (!timestamp) {
    return false; // Don't consider it stale if we can't convert to timestamp
  }

  const now = new Date();
  const createdAtDate = timestamp.toDate();
  const timeDiff = now.getTime() - createdAtDate.getTime();

  return timeDiff > GENERATION_TIMEOUT_MS;
}

export function getActivePendingJobs(jobs: Job[]): Job[] {
  return jobs.filter((job) => {
    if (job.status !== "pending") return false;

    // Exclude stale generations
    if (job.created_at && isJobGenerationStale(job.created_at)) {
      return false;
    }

    return true;
  });
}

export function getStalePendingJobs(jobs: Job[]): Job[] {
  return jobs.filter((job) => {
    if (job.status !== "pending") return false;

    // Only include stale generations
    if (job.created_at && isJobGenerationStale(job.created_at)) {
      return true;
    }

    return false;
  });
}
